import {AfterContentInit, Component, Injectable, OnInit} from '@angular/core';
import { UserService } from '../services/user.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { User } from '../models/user';
import { ActivatedRoute, Router } from '@angular/router';
import { AppConfig } from '../app-config/app.config';
import { NgOption } from '@ng-select/ng-select';
import { AnalyticService } from '../services/analytic.service';
import { MixpanelService } from '../services/mixpanel.service';
import { CommonPasswordService } from '../services/common.password.service';

@Component({
  selector: 'app-sign-up',
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.css']
})
export class SignUpComponent implements OnInit, AfterContentInit {

  emailRegex = new RegExp(/^[a-zA-Z0-9._+!#$%&'*/=?^`{|}~\-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/);
  userRegistrationForm: FormGroup;
  user: User;
  roles: NgOption[];
  passwordLengthValid = false;
  passwordCaseValid = false;
  passwordNumberValid = false;
  passwordSpecialCharacterValid = false;
  passwordContainsRepeatFieldsValid = false;
  passwordContainCommonWordsValid = false;
  passwordMatchValid = false;
  displayValue = true;
  emailDuplicate = false;

  signInLink: string;
  termsAndConditionsLink: string;
  featureFlagCompanyValidation: boolean;
  _window: any;

  campaignId: string;
  cookieCampaignId: string;
  constructor(
    private userService: UserService,
    private appConfig: AppConfig,
    private router: Router,
    private route: ActivatedRoute,
    private analyticsService: AnalyticService,
    private mixpanelService: MixpanelService,
    private passwordService: CommonPasswordService,
  ) {
    this._window = window;
  }

  private static getCampaignIdFromCookie(): string {
    const regExpMatchArray = document.cookie.match(new RegExp('(^| )' + 'campaign' + '=([^;]+)'));
    if (regExpMatchArray) {
      const length = regExpMatchArray.length;
      const prefix = 'hiddenCampaignID=';
      for (let i = 0; i < length; i++) {
        if (regExpMatchArray[i].startsWith(prefix)) {
          return regExpMatchArray[i].split('&')[0].split('=')[1];
        }
      }
    }
    return null;
  }

  ngOnInit(): void {
    this.analyticsService.signUpPageView();
    this.featureFlagCompanyValidation = this.appConfig.config.featureFlagCompanyValidation;
    const companyValidators = [Validators.required];
    if (this.featureFlagCompanyValidation) {
      companyValidators.push(Validators.pattern('^[a-zA-Z0-9\\s]*$'));
    }

    this.userRegistrationForm = new FormGroup({
      firstName: new FormControl(this.userService.getUser() ? this.userService.getUser().firstName : '',
        Validators.required),
      lastName: new FormControl(this.userService.getUser() ? this.userService.getUser().lastName : '',
        Validators.required),
      role: new FormControl(this.userService.getUser() ? this.userService.getUser().role : null,
        Validators.required),
      emailAddress: new FormControl(this.userService.getUser() ? this.userService.getUser().emailAddress : '',
        [Validators.required, Validators.pattern(this.emailRegex)]),
      company: new FormControl(this.userService.getUser() ? this.userService.getUser().company : '',
        companyValidators),
      password: new FormControl(this.userService.getUser() ? this.userService.getUser().password : '',
        [Validators.required, Validators.minLength(8)]),
      confirmPassword: new FormControl(this.userService.getUser() ? this.userService.getUser().confirmPassword : '',
        Validators.required)
    });

    this.signInLink = `${this.appConfig.config.pingfederateLoginUrl}&referrer=Sign Up`;
    this.termsAndConditionsLink = this.appConfig.config.termsAndConditionsUrl;
    this.roles = [
      {name: 'Appraiser'},
      {name: 'Construction'},
      {name: 'Government'},
      {name: 'Insurance Provider'},
      {name: 'Legal'},
      {name: 'Mortgage Lender'},
      {name: 'Real Estate Agent/Broker'},
      {name: 'Real Estate Investor'},
      {name: 'Title Company'},
      {name: '- Additional Roles -', 'disabled': true},
      {name: 'Accounting/CPA'},
      {name: 'Capital Markets'},
      {name: 'Collections'},
      {name: 'Credit Services', },
      {name: 'Data Provider/Reseller'},
      {name: 'Energy/Utility Provider'},
      {name: 'Engineering'},
      {name: 'Higher Education'},
      {name: 'Homeowner'},
      {name: 'Property Manager'},
      {name: 'Risk Management'},
    ];

    this.campaignId = this.getCampaignIdQueryParam();
  }

  ngAfterContentInit(): void {
    this.mixpanelService.track('Page Viewed', {
      'Page Name': 'Sign Up',
      'Previous Page': document.referrer || '/'
    });
  }

  getCampaignIdQueryParam() {
    if (this.route && this.route.snapshot && this.route.snapshot.queryParams) {
      return this.route.snapshot.queryParams['WT.mc_id'];
    }
  }

  navigateToTermsAndConditionsWithUser() {
    if (this.userRegistrationForm.invalid) {
      Object.keys(this.userRegistrationForm.controls).forEach(key => {
        this.userRegistrationForm.get(key).markAsTouched();
      });
      return;
    }

    if (this.userRegistrationForm.valid && !this.emailDuplicate && this.isPasswordValid() && this.passwordMatchValid) {
      this.user = new User();
      this.user.firstName = this.userRegistrationForm.get('firstName').value.trim();
      this.user.lastName = this.userRegistrationForm.get('lastName').value.trim();
      this.user.role = this.userRegistrationForm.get('role').value;
      this.user.emailAddress = this.userRegistrationForm.get('emailAddress').value;
      this.user.company = this.userRegistrationForm.get('company').value.trim();
      this.user.password = this.userRegistrationForm.get('password').value;
      this.user.confirmPassword = this.userRegistrationForm.get('confirmPassword').value;

      this.userService.setUser(this.user);

      if (this.campaignId) {
        this.router.navigate(['terms-and-conditions'], {queryParams: {'WT.mc_id': this.campaignId}});
      } else {
        this.cookieCampaignId = SignUpComponent.getCampaignIdFromCookie();
        if (this.cookieCampaignId != null) {
          this.router.navigate(['terms-and-conditions'], {queryParams: {'WT.mc_id': this.cookieCampaignId}});
        } else {
          this.router.navigate(['terms-and-conditions']);
        }
      }
    }
  }

  isFirstNameMissing() {
    return this.userRegistrationForm.controls.firstName.invalid && this.userRegistrationForm.controls.firstName.touched;
  }

  isLastNameMissing() {
    return this.userRegistrationForm.controls.lastName.invalid && this.userRegistrationForm.controls.lastName.touched;
  }

  isRoleNotChosen() {
    return this.userRegistrationForm.controls.role.invalid && this.userRegistrationForm.controls.role.touched;
  }

  displayEmailRedBorder(field: string) {
    return this.isFieldEmpty(field) || !this.isValidEmail(field) || this.emailDuplicate;
  }

  isCompanyMissing() {
    return this.userRegistrationForm.controls.company.invalid &&
      this.userRegistrationForm.controls.company.touched &&
      this.userRegistrationForm.controls.company.value.length === 0;
  }

  isCompanyInvalid() {
    if (this.featureFlagCompanyValidation) {
      return this.userRegistrationForm.controls.company.invalid &&
        this.userRegistrationForm.controls.company.touched &&
        this.userRegistrationForm.controls.company.value.length > 0;
    } else {
      return false;
    }
  }

  isPasswordMissing() {
    return this.isFieldEmpty(this.userRegistrationForm.get('password').value) && this.userRegistrationForm.controls.password.touched;
  }

  isConfirmPasswordMissing() {
    return this.isFieldEmpty(this.userRegistrationForm.get('confirmPassword').value)
      && this.userRegistrationForm.controls.confirmPassword.touched;
  }

  displayPasswordValidation() {
    return this.displayValue = false;
  }


  isFieldEmpty(field: string) {
    return field === '';
  }

  isValidEmail(field: string) {
    return this.emailRegex.test(field);
  }

  isEmailDuplicate(email: string) {
    if (this.isValidEmail(email)) {
      this.userService.isEmailDuplicate(email).subscribe((response) => {
        if (response) {
          this.emailDuplicate = true;
        } else {
          this.emailDuplicate = false;
        }
      });
    }
  }

  isPasswordLengthValid(field: string) {
    if (field.length >= 8) {
      this.passwordLengthValid = true;
      return true;
    } else {
      this.passwordLengthValid = false;
      return false;
    }
  }

  isPasswordCaseValid(field: string) {
    const passwordCaseRegex = new RegExp(/^(?=.*[a-z])(?=.*[A-Z]).+$/);
    if (passwordCaseRegex.test(field)) {
      this.passwordCaseValid = true;
      return true;
    } else {
      this.passwordCaseValid = false;
      return false;
    }
  }

  isPasswordNumberValid(field: string) {
    const passwordNumberRegex = new RegExp(/^(?=.*\d).+$/);
    if (passwordNumberRegex.test(field)) {
      this.passwordNumberValid = true;
      return true;
    } else {
      this.passwordNumberValid = false;
      return false;
    }
  }

  isPasswordSpecialCharacterValid(field: string) {
    const passwordSpecialCharacterRegex = new RegExp(/[-!$%^&*()_+|~=`{}#@\[\]:";'<>?,.\/]/);
    if (passwordSpecialCharacterRegex.test(field)) {
      this.passwordSpecialCharacterValid = true;
      return true;
    } else {
      this.passwordSpecialCharacterValid = false;
      return false;
    }
  }

  containsRepeatFields(field: string) {
    const firstName = this.userRegistrationForm.get('firstName').value;
    const lastName = this.userRegistrationForm.get('lastName').value;
    const emailAddress = this.userRegistrationForm.get('emailAddress').value;

    if (field.toLowerCase().includes(firstName.toLowerCase())
      || field.toLowerCase().includes(lastName.toLowerCase())
      || field.toLowerCase().includes(emailAddress.toLowerCase())) {
      this.passwordContainsRepeatFieldsValid = false;
      return true;
    } else {
      this.passwordContainsRepeatFieldsValid = true;
      return false;
    }
  }

  containsCommonWords(field: string) {
    const isCommonPassword = this.passwordService.isCommonPassword(field);
    this.passwordContainCommonWordsValid = !isCommonPassword;
    return isCommonPassword;
  }

  doesPasswordMatch() {
    if (this.userRegistrationForm.get('password').value === this.userRegistrationForm.get('confirmPassword').value) {
      this.passwordMatchValid = true;
      return true;
    } else {
      this.passwordMatchValid = false;
      return false;
    }
  }

  isPasswordValid() {
    return this.passwordLengthValid
      && this.passwordCaseValid
      && this.passwordNumberValid
      && this.passwordContainsRepeatFieldsValid
      && this.passwordContainCommonWordsValid
      && this.passwordSpecialCharacterValid;
  }
}
